import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, AlertCircle, CheckCircle, Trash2, DollarSign, RefreshCw, MessageCircle } from "lucide-react";

// Helper function to extract error message from API response
const getErrorMessage = (error, defaultMsg = "Error en la operación") => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // If detail is an array (Pydantic validation errors)
    if (Array.isArray(detail)) {
      return detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
    }
    // If detail is a string
    if (typeof detail === 'string') {
      return detail;
    }
  }
  return defaultMsg;
};

const PAYMENT_TYPES = [
  { value: "venta", label: "Venta (Efectivo)" },
  { value: "sinpe", label: "SINPE Móvil" },
  { value: "banco_tucan", label: "Banco Tucán" },
  { value: "banco_payser", label: "Banco Payser" },
  { value: "banco_western", label: "Western Union" },
  { value: "banco_rapibac", label: "Rapibac" },
  { value: "banco_otro", label: "Otro Banco" },
  { value: "salida", label: "Salida de Dinero" },
  { value: "no_afecta_caja", label: "No Afecta Caja" },
];

// Métodos de pago para el registro rápido de gastos
const EXPENSE_PAYMENT_METHODS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "sinpe", label: "SINPE" },
  { value: "transferencia", label: "Transferencia bancaria" },
  { value: "salida_banco", label: "Salida de banco" },
  { value: "otro", label: "Otro método de pago" },
];

export default function Expenses({ user }) {
  const { selectedBox } = useBox();
  const [expenses, setExpenses] = useState([]);
  const [dueSoon, setDueSoon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [activeTab, setActiveTab] = useState("pendiente");
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  const [formData, setFormData] = useState({
    recipient: "",
    description: "",
    amount_colones: 0,
    amount_dollars: 0,
    due_date: "",
    document_number: "",
    category: "negocio",
    payment_method: "efectivo",
    affects_balance: false
  });
  
  const [paymentData, setPaymentData] = useState({
    transaction_type: "salida",
    payment_method: "",
    transaction_number: "",
    is_recurring: false,
    amount_colones: 0,
    amount_dollars: 0
  });

  const fetchExpenses = async () => {
    try {
      const url = selectedBox ? `/expenses?box_id=${selectedBox.id}` : '/expenses';
      const dueSoonUrl = selectedBox ? `/expenses/due-soon?box_id=${selectedBox.id}` : '/expenses/due-soon';
      
      const response = await axiosInstance.get(url);
      setExpenses(response.data);
      
      const dueSoonResponse = await axiosInstance.get(dueSoonUrl);
      setDueSoon(dueSoonResponse.data);
    } catch (error) {
      console.error("Error fetching expenses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBox) {
      fetchExpenses();
      const interval = setInterval(fetchExpenses, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, [selectedBox]);

  const handleCreateExpense = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }

    // Confirmación explícita para evitar asignar el gasto a una caja equivocada
    // cuando Master tiene varias cajas y olvida cambiar el selector.
    const confirmed = window.confirm(
      `Este gasto se asignará a la caja: "${selectedBox.name}".\n\n` +
      `El cajero de esa caja verá la alerta cuando entre al sistema.\n\n` +
      `¿Continuar?`
    );
    if (!confirmed) return;

    try {
      // Send box_id in the body
      const expenseData = {
        ...formData,
        box_id: selectedBox.id
      };
      await axiosInstance.post('/expenses', expenseData);
      toast.success(
        `Gasto creado en caja ${selectedBox.name}. El cajero de esa caja verá el aviso al entrar al sistema.`,
        { duration: 6000 }
      );
      setShowCreateDialog(false);
      setFormData({ recipient: "", description: "", amount_colones: 0, amount_dollars: 0, due_date: "", document_number: "", category: "negocio", payment_method: "efectivo", affects_balance: false });
      fetchExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al crear gasto"));
    }
  };

  const handlePayExpense = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }
    
    try {
      await axiosInstance.patch(`/expenses/${selectedExpense.id}/pay`, {
        ...paymentData,
        box_id: selectedBox.id
      });
      toast.success(paymentData.is_recurring ? "Gasto pagado y creado para el próximo mes" : "Gasto pagado exitosamente");
      setShowPayDialog(false);
      setSelectedExpense(null);
      setPaymentData({ transaction_type: "salida", payment_method: "", transaction_number: "", is_recurring: false });
      fetchExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al pagar gasto"));
    }
  };

  const handleDeleteExpense = async () => {
    try {
      await axiosInstance.delete(`/expenses/${selectedExpense.id}?password=${deletePassword}`);
      toast.success("Gasto eliminado");
      setShowDeleteDialog(false);
      setSelectedExpense(null);
      setDeletePassword("");
      fetchExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar gasto"));
    }
  };

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CR');
  };

  const isDueSoon = (dueDate) => {
    const due = new Date(dueDate);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return due <= tomorrow;
  };

  const isOverdue = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  };

  // Returns true ONLY when the expense is due within ≤1 day or already overdue (not before)
  const isWhatsappAlertEligible = (dueDate) => {
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 1; // overdue, today or tomorrow
  };

  const handleWhatsAppAlert = (expense) => {
    const number = selectedBox?.expenses_alert_whatsapp;
    if (!number) {
      toast.error("Esta caja no tiene un número de WhatsApp para alertas. Configúralo en Cajas → 📱.");
      return;
    }
    // Calculate diff days exactly
    const due = new Date(expense.due_date);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));

    let status, action;
    if (diffDays < 0) {
      status = `🚨 VENCIDO desde el ${formatDate(expense.due_date)}`;
      action = `Por favor procesa el pago lo antes posible.`;
    } else if (diffDays === 0) {
      status = `⚠️ VENCE HOY`;
      action = `Recordatorio: este gasto vence HOY. Por favor procesa el pago.`;
    } else if (diffDays === 1) {
      status = `⏰ VENCE MAÑANA`;
      action = `Recordatorio: este gasto vence MAÑANA.`;
    } else {
      status = `📅 POR VENCER (en ${diffDays} días)`;
      action = `Recordatorio anticipado de pago.`;
    }

    const lines = [
      `${status}`,
      ``,
      `*Caja:* ${selectedBox.name}`,
      `*Gasto a:* ${expense.recipient}`,
      `*Descripción:* ${expense.description || '—'}`,
    ];
    if (expense.document_number) lines.push(`*Documento:* ${expense.document_number}`);
    if (expense.amount_colones) lines.push(`*Monto:* ${formatCurrency(expense.amount_colones)}`);
    if (expense.amount_dollars) lines.push(`*Monto USD:* ${formatCurrency(expense.amount_dollars, "dollars")}`);
    lines.push(`*Fecha de pago:* ${formatDate(expense.due_date)}`);
    lines.push(``);
    lines.push(action);

    const text = encodeURIComponent(lines.join('\n'));
    const cleaned = number.replace(/[^\d]/g, "");
    const url = `https://wa.me/${cleaned}?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const filteredExpenses = expenses.filter(e => e.status === activeTab);

  // Totales por categoría (Negocio/Local vs Personal/Propio) sobre TODOS los gastos
  // registrados de la caja (pendientes + pagados), con el total general combinado.
  const sumByCategory = (cat) => expenses
    .filter(e => (e.category || "negocio") === cat)
    .reduce((a, e) => ({ c: a.c + (e.amount_colones || 0), d: a.d + (e.amount_dollars || 0) }), { c: 0, d: 0 });
  const totalNegocio = sumByCategory("negocio");
  const totalPersonal = sumByCategory("personal");
  const totalGeneral = { c: totalNegocio.c + totalPersonal.c, d: totalNegocio.d + totalPersonal.d };

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Gestión de Gastos
            </h1>
            <p className="text-slate-600">Administra gastos recurrentes y pagos</p>
          </div>
          
          {/* Clock Display */}
          <div className="flex flex-col items-center px-4 py-1 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600 font-medium">
              {currentTime.toLocaleDateString('es-CR', { 
                weekday: 'short', 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              })}
            </div>
            <div className="text-lg font-bold text-blue-700">
              {currentTime.toLocaleTimeString('es-CR', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
              })}
            </div>
          </div>
        </div>
        
        <Button
          data-testid="create-expense-button"
          onClick={() => setShowCreateDialog(true)}
          className="bg-blue-600 hover:bg-blue-700 gap-2"
        >
          <Plus className="w-4 h-4" />
          Nuevo Gasto
        </Button>
        <Button
          data-testid="open-suppliers-btn"
          onClick={() => window.location.href = '/suppliers'}
          variant="outline"
          className="gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
        >
          <Plus className="w-4 h-4" />
          Registrar Factura Proveedor
        </Button>
      </div>

      {/* Resumen de totales por categoría */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="expenses-summary">
        <div className="rounded-xl p-4 bg-emerald-50 border border-emerald-200" data-testid="expenses-total-negocio">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Gastos del Negocio / Local</p>
          <p className="text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(totalNegocio.c)}</p>
          {totalNegocio.d > 0 && <p className="text-sm text-emerald-700">${totalNegocio.d.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</p>}
        </div>
        <div className="rounded-xl p-4 bg-amber-50 border border-amber-200" data-testid="expenses-total-personal">
          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Gastos Personales (Propios)</p>
          <p className="text-2xl font-bold text-amber-800 mt-1">{formatCurrency(totalPersonal.c)}</p>
          {totalPersonal.d > 0 && <p className="text-sm text-amber-700">${totalPersonal.d.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</p>}
        </div>
        <div className="rounded-xl p-4 bg-blue-600 text-white border border-blue-700 shadow" data-testid="expenses-total-general">
          <p className="text-xs font-semibold text-blue-100 uppercase tracking-wide">Total General (Negocio + Personal)</p>
          <p className="text-2xl font-bold mt-1">{formatCurrency(totalGeneral.c)}</p>
          {totalGeneral.d > 0 && <p className="text-sm text-blue-100">${totalGeneral.d.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</p>}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pendiente">Pendientes</TabsTrigger>
          <TabsTrigger value="pagado">Pagados</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredExpenses.length === 0 ? (
              <Card className="col-span-full bg-white/80 backdrop-blur-xl border-slate-200">
                <CardContent className="py-12">
                  <p className="text-center text-slate-500">No hay gastos {activeTab === "pendiente" ? "pendientes" : "pagados"}</p>
                </CardContent>
              </Card>
            ) : (
              filteredExpenses.map((expense) => {
                const overdueStatus = expense.status === "pendiente" && isOverdue(expense.due_date);
                return (
                <Card
                  key={expense.id}
                  data-testid={`expense-${expense.id}`}
                  className={`bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg hover:shadow-xl transition-shadow ${
                    overdueStatus ? 'border-l-4 border-l-red-500' : 
                    expense.status === "pendiente" && isDueSoon(expense.due_date) ? 'border-l-4 border-l-orange-500' : ''
                  }`}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between text-lg">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                        {expense.recipient}
                        <span
                          data-testid={`expense-category-badge-${expense.id}`}
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            (expense.category || "negocio") === "personal"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {(expense.category || "negocio") === "personal" ? "Personal" : "Negocio"}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {expense.status === "pendiente" && isWhatsappAlertEligible(expense.due_date) && selectedBox?.expenses_alert_whatsapp && (
                          <Button
                            data-testid={`whatsapp-alert-${expense.id}`}
                            size="sm"
                            onClick={() => handleWhatsAppAlert(expense)}
                            className="bg-emerald-500 hover:bg-emerald-600"
                            title="Avisar por WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {expense.status === "pendiente" && (
                          <Button
                            data-testid={`pay-expense-${expense.id}`}
                            size="sm"
                            onClick={() => {
                              setSelectedExpense(expense);
                              setPaymentData({
                                transaction_type: "salida",
                                payment_method: "",
                                transaction_number: "",
                                is_recurring: false,
                                amount_colones: expense.amount_colones || 0,
                                amount_dollars: expense.amount_dollars || 0
                              });
                              setShowPayDialog(true);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {user.role === 'master' && expense.status === "pendiente" && (
                          <Button
                            data-testid={`delete-expense-${expense.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedExpense(expense);
                              setShowDeleteDialog(true);
                            }}
                            className="hover:bg-red-50 hover:text-red-600"
                            title="Eliminar gasto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {overdueStatus && (
                      <div className="p-3 bg-red-50 border-2 border-red-400 rounded-lg mb-3">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600" />
                          <p className="font-bold text-red-800">⚠️ FECHA DE PAGO VENCIDA</p>
                        </div>
                        <p className="text-sm text-red-700 mt-1">
                          Este gasto ya pasó su fecha de pago. Por favor, procesa el pago lo antes posible.
                        </p>
                      </div>
                    )}
                    <p className="text-sm text-slate-700">{expense.description}</p>
                    {expense.document_number && (
                      <p className="text-xs text-slate-600">
                        <span className="font-semibold">Doc:</span> {expense.document_number}
                      </p>
                    )}
                    <div className="space-y-1">
                      <p className="font-semibold text-blue-600">{formatCurrency(expense.amount_colones)}</p>
                      <p className="text-sm text-green-600">{formatCurrency(expense.amount_dollars, "dollars")}</p>
                    </div>
                    <div className={`text-xs ${
                      overdueStatus ? 'text-red-600 font-bold' :
                      expense.status === "pendiente" && isDueSoon(expense.due_date) ? 'text-orange-600 font-semibold' : 'text-slate-500'
                    }`}>
                      Pago: {formatDate(expense.due_date)}
                    </div>
                    {expense.status === "pagado" && (
                      <div className="text-xs text-green-600 pt-2 border-t">
                        Pagado el: {formatDate(expense.payment_date)}
                        <br />
                        Método: {expense.payment_method}
                        <br />
                        Trámite: {expense.transaction_number}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Expense Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-expense-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Gasto</DialogTitle>
            <DialogDescription>Registra un gasto y guarda el historial</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Banner: caja destino del gasto */}
            <div
              data-testid="expense-target-box-banner"
              className="flex items-center gap-3 p-3 rounded-lg border-2 border-blue-300 bg-blue-50"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                📦
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Caja destino</p>
                <p className="text-base font-bold text-blue-900 truncate">
                  {selectedBox?.name || "(ninguna seleccionada)"}
                </p>
                <p className="text-xs text-blue-700 mt-0.5">
                  El cajero de esta caja verá la alerta cuando entre al sistema.
                  Si querés cambiar de caja, usá el selector arriba a la derecha.
                </p>
              </div>
            </div>

            <div>
              <Label>Tipo de gasto</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  data-testid="expense-category-negocio"
                  onClick={() => setFormData({ ...formData, category: "negocio" })}
                  className={formData.category === "negocio" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}
                >
                  Negocio / Local
                </Button>
                <Button
                  type="button"
                  data-testid="expense-category-personal"
                  onClick={() => setFormData({ ...formData, category: "personal" })}
                  className={formData.category === "personal" ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}
                >
                  Personal (Propio)
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="recipient">Nombre de Compañía / Comercio</Label>
                <Input
                  id="recipient"
                  data-testid="expense-recipient-input"
                  value={formData.recipient}
                  onChange={(e) => setFormData({...formData, recipient: e.target.value})}
                  className="mt-1.5"
                  placeholder="Ej: ICE, Gasolinera, Súper"
                />
              </div>
              <div>
                <Label htmlFor="document_number">N° Factura / Recibo</Label>
                <Input
                  id="document_number"
                  data-testid="expense-document-number-input"
                  value={formData.document_number}
                  onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                  className="mt-1.5"
                  placeholder="Ej: #123456, NIS-789"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                data-testid="expense-description-input"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1.5"
                placeholder="Detalles del gasto"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amount_colones">Monto en Colones</Label>
                <Input
                  id="amount_colones"
                  data-testid="expense-colones-input"
                  type="number"
                  step="0.01"
                  value={formData.amount_colones}
                  onChange={(e) => setFormData({...formData, amount_colones: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
              {selectedBox?.uses_dollars !== false && (
                <div>
                  <Label htmlFor="amount_dollars">Monto en Dólares</Label>
                  <Input
                    id="amount_dollars"
                    data-testid="expense-dollars-input"
                    type="number"
                    step="0.01"
                    value={formData.amount_dollars}
                    onChange={(e) => setFormData({...formData, amount_dollars: parseFloat(e.target.value) || 0})}
                    className="mt-1.5"
                  />
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="payment_method">Método de pago</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="expense-payment-method-trigger">
                    <SelectValue placeholder="Selecciona método" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value} data-testid={`expense-pm-${m.value}`}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="due_date">Fecha del gasto (opcional)</Label>
                <Input
                  id="due_date"
                  data-testid="expense-due-date-input"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  className="mt-1.5"
                />
                <p className="text-xs text-slate-500 mt-1">Vacía = hoy. Solo historial.</p>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 bg-slate-50">
              <input
                type="checkbox"
                id="affects_balance"
                data-testid="expense-affects-balance"
                className="mt-1 h-4 w-4"
                checked={formData.affects_balance}
                onChange={(e) => setFormData({ ...formData, affects_balance: e.target.checked })}
              />
              <label htmlFor="affects_balance" className="text-sm text-slate-700 cursor-pointer">
                <span className="font-medium">Afecta saldo (descontar de banco/caja)</span>
                <br />
                <span className="text-xs text-slate-500">
                  Si lo dejas desmarcado, el gasto queda solo como registro/historial y NO toca los saldos.
                  {formData.payment_method === "efectivo"
                    ? " Con Efectivo se descuenta de la caja abierta."
                    : " Con métodos bancarios se descuenta del banco/bóveda asignado a la caja."}
                </span>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-create-expense-button"
              onClick={handleCreateExpense}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!formData.recipient || !formData.description}
            >
              Crear Gasto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Expense Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent data-testid="pay-expense-dialog">
          <DialogHeader>
            <DialogTitle>Pagar Gasto</DialogTitle>
            <DialogDescription>
              Selecciona el tipo de movimiento para pagar: {selectedExpense?.recipient}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* NEW: Editable amount fields */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 mb-3">
                <strong>Monto Aproximado Original:</strong> ₡{(selectedExpense?.amount_colones || 0).toLocaleString()} / ${(selectedExpense?.amount_dollars || 0).toFixed(2)}
              </p>
              <p className="text-xs text-yellow-700 mb-3">
                Ajusta el monto si es necesario (el monto real puede diferir del aproximado):
              </p>
              <div className={selectedBox?.uses_dollars !== false ? "grid grid-cols-2 gap-3" : ""}>
                <div>
                  <Label htmlFor="payment_amount_colones" className="text-xs">Monto Real (₡)</Label>
                  <Input
                    id="payment_amount_colones"
                    type="number"
                    step="0.01"
                    value={paymentData.amount_colones}
                    onChange={(e) => setPaymentData({...paymentData, amount_colones: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                {selectedBox?.uses_dollars !== false && (
                  <div>
                    <Label htmlFor="payment_amount_dollars" className="text-xs">Monto Real ($)</Label>
                    <Input
                      id="payment_amount_dollars"
                      type="number"
                      step="0.01"
                      value={paymentData.amount_dollars}
                      onChange={(e) => setPaymentData({...paymentData, amount_dollars: parseFloat(e.target.value) || 0})}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="transaction_type">Tipo de Movimiento</Label>
              <Select
                value={paymentData.transaction_type}
                onValueChange={(value) => setPaymentData({...paymentData, transaction_type: value})}
              >
                <SelectTrigger data-testid="payment-type-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="payment_method">Medio de Pago</Label>
              <Input
                id="payment_method"
                data-testid="payment-method-input"
                value={paymentData.payment_method}
                onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                className="mt-1.5"
                placeholder="Ej: Transferencia, Efectivo, Cheque"
              />
            </div>
            
            <div>
              <Label htmlFor="transaction_number">Número de Trámite</Label>
              <Input
                id="transaction_number"
                data-testid="transaction-number-input"
                value={paymentData.transaction_number}
                onChange={(e) => setPaymentData({...paymentData, transaction_number: e.target.value})}
                className="mt-1.5"
                placeholder="Ej: #123456"
              />
            </div>
            
            <div className="flex items-center space-x-2 pt-2 border-t">
              <Checkbox
                id="is_recurring"
                data-testid="recurring-checkbox"
                checked={paymentData.is_recurring}
                onCheckedChange={(checked) => setPaymentData({...paymentData, is_recurring: checked})}
              />
              <Label htmlFor="is_recurring" className="flex items-center gap-2 cursor-pointer text-sm font-normal">
                <RefreshCw className="w-4 h-4 text-blue-600" />
                <span>Es un pago mensual recurrente (crear automáticamente para el próximo mes)</span>
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-pay-expense-button"
              onClick={handlePayExpense}
              className="bg-green-600 hover:bg-green-700"
              disabled={!paymentData.payment_method || !paymentData.transaction_number}
            >
              Pagar Gasto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Expense Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-expense-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar Gasto</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar este gasto?
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="delete_password">Contraseña Master</Label>
            <Input
              id="delete_password"
              data-testid="delete-expense-password-input"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="mt-1.5"
              placeholder="Ingresa la contraseña master"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setDeletePassword("");
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-expense-button"
              onClick={handleDeleteExpense}
              variant="destructive"
              disabled={!deletePassword}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}